import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RCTIPage from './pages/RCTIPage';
import DocumentsPage from './pages/DocumentsPage';
import ContractsPage from './pages/ContractsPage';
import FleetPage from './pages/FleetPage';
import PersonnelPage from './pages/PersonnelPage';
import DriversPage from './pages/DriversPage';
import NightShiftPage from './pages/NightShiftPage';
import SettingsPage from './pages/SettingsPage';
import GCCDReportPage from './pages/GCCDReportPage';
import EndOfShiftPage from './pages/EndOfShiftPage';
import HourlyDriversPage from './pages/HourlyDriversPage';
import RevenuePage from './pages/RevenuePage';
import RosterPage from './pages/RosterPage';
import DirectEntryPage from './pages/DirectEntryPage';
import MSOPage from './pages/MSOPage';
import FuelWorkbenchPage from './pages/FuelWorkbenchPage';
import DriverLogPage from './pages/DriverLogPage';
import PracticeFinishPage from './pages/PracticeFinishPage';
import ShiftApprovalsPage from './pages/ShiftApprovalsPage';
import DriverRoutesPage from './pages/DriverRoutesPage';
import DepotMapPage from './pages/DepotMapPage';
import TaxInvoicesPage from './pages/TaxInvoicesPage';
import ImportParcelsPage from './pages/ImportParcelsPage';
import ParcelAuditPage from './pages/ParcelAuditPage';
import ScannerAuditPage from './pages/ScannerAuditPage';
import ScannersPage from './pages/ScannersPage';
import DriverActivitySummaryPage from './pages/DriverActivitySummaryPage';
import ScannerPhotosPage from './pages/ScannerPhotosPage';
import DailySnapshotPage from './pages/DailySnapshotPage';
import NightSortPage from './pages/NightSortPage';
import AwardRatesPage from './pages/AwardRatesPage';
import APIntelligencePage from './pages/APIntelligencePage';
import AfternoonCheckPage from './pages/AfternoonCheckPage';
import LoginCheckPage from './pages/LoginCheckPage';
import VehicleServicePage from './pages/VehicleServicePage';
import OnboardingPage from './pages/OnboardingPage';
import InfoRequestPage from './pages/InfoRequestPage';
import ApplyPage from './pages/ApplyPage';
import SortersPortalPage from './pages/SortersPortalPage';
import SortersAdminPage from './pages/SortersAdminPage';
import SortRosterPortalPage from './pages/SortRosterPortalPage';
import TrafficFinesPage from './pages/TrafficFinesPage';
import VehicleClaimsPage from './pages/VehicleClaimsPage';
import DriverAccidentsPage from './pages/DriverAccidentsPage';
import DriverReviewPage from './pages/DriverReviewPage';
import IndoorTimePage from './pages/IndoorTimePage';
import CompliancePage from './pages/CompliancePage';
import ImmigrationPage from './pages/ImmigrationPage';
import WahrImportPage from './pages/WahrImportPage';
import PeakPlanningPage from './pages/PeakPlanningPage';
import TenderModelPage from './pages/TenderModelPage';
import WahrComparisonPage from './pages/WahrComparisonPage';
import RetrainingPage from './pages/RetrainingPage';
import SafetyPage from './pages/SafetyPage';
import ReceiptsPage from './pages/ReceiptsPage';
import AtoPlansPage from './pages/AtoPlansPage';
import UniformRegisterPage from './pages/UniformRegisterPage';
import MailStoppersPage from './pages/MailStoppersPage';
import StandingOrdersPage from './pages/StandingOrdersPage';
import CustomerCasesPage from './pages/CustomerCasesPage';
import PushDeepLink from './components/PushDeepLink';
import DriverIssuesPage from './pages/DriverIssuesPage';
import LicensingPage from './pages/LicensingPage';
import OnboardingTasksPage from './pages/OnboardingTasksPage';
import DailyTasksPage from './pages/DailyTasksPage';
import MelbourneCupPage from './pages/MelbourneCupPage';
import NailsPortalPage from './pages/NailsPortalPage';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/global.css';

// The portals stand alone — no sidebar, no way out of a crashed screen. Each gets its
// own boundary so a render error shows a message and a reload button on the phone
// instead of the white screen a driver has no way to interpret. The admin pages are
// wrapped inside Layout instead (around the Outlet), so the nav survives a crash and
// the user can just click somewhere else.
const portal = (label, element) => (
  <ErrorBoundary variant="portal" label={label}>{element}</ErrorBoundary>
);

export default function App() {
  return (
    <AppProvider>
      <Router>
        <PushDeepLink />
        <Routes>
          <Route path="/login"      element={portal('login', <LoginPage />)} />
          <Route path="/driver-log" element={portal('driver-log', <DriverLogPage />)} />
          <Route path="/practice"   element={portal('practice', <PracticeFinishPage />)} />
          <Route path="/sorters"    element={portal('sorters', <SortersPortalPage />)} />
          <Route path="/sort-roster" element={portal('sort-roster', <SortRosterPortalPage />)} />
          <Route path="/onboard"    element={portal('onboard', <OnboardingPage />)} />
          <Route path="/provide-info" element={portal('provide-info', <InfoRequestPage />)} />
          <Route path="/apply"      element={portal('apply', <ApplyPage />)} />
          {/* Nails by Asha — a SEPARATE business (Asha's own ABN) living in the engine as
              a tenant. Standalone like the other portals, with its own hp_nails session. */}
          <Route path="/nails"      element={portal('nails', <NailsPortalPage />)} />
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"      element={<DashboardPage />} />
            <Route path="daily-tasks"    element={<DailyTasksPage />} />
            <Route path="melbourne-cup"  element={<MelbourneCupPage />} />
            <Route path="rcti"           element={<RCTIPage />} />
            <Route path="gccd-report"    element={<GCCDReportPage />} />
            <Route path="end-of-shift"   element={<EndOfShiftPage />} />
            <Route path="hourly-drivers" element={<HourlyDriversPage />} />
            <Route path="driver-review"  element={<DriverReviewPage />} />
            <Route path="revenue"        element={<RevenuePage />} />
            <Route path="licensing"      element={<LicensingPage />} />
            <Route path="import-parcels" element={<ImportParcelsPage />} />
            <Route path="parcel-audit"   element={<ParcelAuditPage />} />
            <Route path="scanner-audit"  element={<ScannerAuditPage />} />
            <Route path="scanners"       element={<ScannersPage />} />
            <Route path="roster"         element={<RosterPage />} />
            <Route path="direct-entry"   element={<DirectEntryPage />} />
            <Route path="documents"      element={<DocumentsPage />} />
            <Route path="contracts"      element={<ContractsPage />} />
            <Route path="receipts"       element={<ReceiptsPage />} />
            <Route path="ato-plans"      element={<AtoPlansPage />} />
            <Route path="mail-stoppers"  element={<MailStoppersPage />} />
            <Route path="standing-orders" element={<StandingOrdersPage />} />
            <Route path="customer-cases" element={<CustomerCasesPage />} />
            <Route path="driver-issues"  element={<DriverIssuesPage />} />
            <Route path="fleet"           element={<FleetPage />} />
            <Route path="traffic-fines"   element={<TrafficFinesPage />} />
            <Route path="driver-accidents" element={<DriverAccidentsPage />} />
            <Route path="vehicle-claims"  element={<VehicleClaimsPage />} />
            <Route path="vehicle-service" element={<VehicleServicePage />} />
            <Route path="personnel"      element={<PersonnelPage />} />
            <Route path="onboarding"     element={<OnboardingTasksPage />} />
            <Route path="drivers"        element={<DriversPage />} />
            <Route path="uniforms"       element={<UniformRegisterPage />} />
            <Route path="night-shift"    element={<NightShiftPage />} />
            <Route path="settings"       element={<SettingsPage />} />
            <Route path="mso"            element={<MSOPage />} />
            <Route path="fuel-workbench"   element={<FuelWorkbenchPage />} />
            <Route path="shift-approvals" element={<ShiftApprovalsPage />} />
            <Route path="afternoon-check" element={<AfternoonCheckPage />} />
            <Route path="login-check"     element={<LoginCheckPage />} />
            <Route path="driver-routes"   element={<DriverRoutesPage />} />
            <Route path="depot-map"       element={<DepotMapPage />} />
            <Route path="tax-invoices"           element={<TaxInvoicesPage />} />
            <Route path="driver-activity-summary" element={<DriverActivitySummaryPage />} />
            <Route path="scanner-photos"          element={<ScannerPhotosPage />} />
            <Route path="daily-snapshot"          element={<DailySnapshotPage />} />
            <Route path="indoor-time"             element={<IndoorTimePage />} />
            <Route path="night-sort"              element={<NightSortPage />} />
            <Route path="award-rates"             element={<AwardRatesPage />} />
            <Route path="sorters-admin"           element={<SortersAdminPage />} />
            <Route path="ap-intelligence"         element={<APIntelligencePage />} />
            <Route path="compliance"              element={<CompliancePage />} />
            <Route path="retraining"              element={<RetrainingPage />} />
            <Route path="safety"                  element={<SafetyPage />} />
            {/* The Pre-Card register is a tab of Safety, but it is a page in its own right
                as far as anyone looking for it is concerned — so it gets its own address,
                its own sidebar entry, and it is findable from the dashboard search. */}
            <Route path="pre-card"                element={<SafetyPage initialTab="precard" />} />
            <Route path="incident-reports"        element={<SafetyPage initialTab="incidents" />} />
            <Route path="immigration"             element={<ImmigrationPage />} />
            <Route path="wahr-comparison"         element={<WahrComparisonPage />} />
            <Route path="wahr-import"             element={<WahrImportPage />} />
            <Route path="peak-planning"           element={<PeakPlanningPage />} />
            <Route path="tender-model"            element={<TenderModelPage />} />
          </Route>
        </Routes>
      </Router>
    </AppProvider>
  );
}
